import Braintree from "braintree-web";
import React from "react";

export type BraintreeFields = "number" | "cvv" | "expirationDate" | "postalCode";

export const useBraintree = () => {
  const [client, setClient] = React.useState<Braintree.Client>();
  const [hostedFields, setHostedFields] = React.useState<Braintree.HostedFields>();
  const [dataCollector, setDataCollector] = React.useState<Braintree.DataCollector>();

  const [fields, setFields] = React.useState<
    Record<
      BraintreeFields,
      {
        container?: HTMLElement;
        isFocused?: boolean;
        isEmpty?: boolean;
        isPotentiallyValid?: boolean;
        isValid?: boolean;
      }
    >
  >({
    number: { isEmpty: true },
    cvv: { isEmpty: true },
    expirationDate: { isEmpty: true },
    postalCode: { isEmpty: true },
  });

  const numberRef = React.useRef<HTMLDivElement>();
  const cvvRef = React.useRef<HTMLDivElement>();
  const expDateRef = React.useRef<HTMLDivElement>();
  const postalCodeRef = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    // The cancelled flag bails any init still in flight when the component unmounts so a
    // stale create() doesn't race the next mount and trip HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME.
    // On real unmount, removing the host DOM tears the iframes down with it; we deliberately
    // skip hostedFields.teardown() because braintree-web fires internal events during teardown
    // that re-enter its own torn-down guard and surface as uncaught METHOD_CALLED_AFTER_TEARDOWN.
    let cancelled = false;

    const initBraintree = async () => {
      const client = await Braintree.client.create({
        authorization: import.meta.env.VITE_BRAINTREE_TOKENIZATION_KEY,
      });
      if (cancelled) return;

      const hostedFields = await Braintree.hostedFields.create({
        client,
        styles: {
          input: {
            padding: "8px",
            // Can't use our font, so use a close match
            "font-family": '"Avenir Next", Avenir, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
            "font-size": "16px",
            "line-height": "24px",
            color: "#292929",
          },
        },
        fields: {
          number: { container: numberRef.current, placeholder: "Enter" },
          cvv: { container: cvvRef.current, placeholder: "Enter" },
          expirationDate: { container: expDateRef.current, placeholder: "MM/YY" },
          postalCode: { container: postalCodeRef.current, placeholder: "Enter" },
        },
      });
      if (cancelled) return;

      const dataCollector = await Braintree.dataCollector.create({ client });
      if (cancelled) return;

      setClient(client);
      setHostedFields(hostedFields);
      setDataCollector(dataCollector);
      setFields(hostedFields.getState().fields as any);

      hostedFields.on("blur", ({ fields }) => setFields(fields as any));
      hostedFields.on("cardTypeChange", ({ fields }) => setFields(fields as any));
      hostedFields.on("empty", ({ fields }) => setFields(fields as any));
      hostedFields.on("focus", ({ fields }) => setFields(fields as any));
      hostedFields.on("notEmpty", ({ fields }) => setFields(fields as any));
      hostedFields.on("validityChange", ({ fields }) => setFields(fields as any));
    };

    initBraintree().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, []);

  const getPayload = React.useCallback(async () => {
    if (!hostedFields) {
      console.error("No braintree hosted fields");
      throw "Internal processing error";
    }

    if (!dataCollector) {
      console.error("No braintree data collector");
      throw "Internal processing error";
    }

    try {
      const [payload, deviceData] = await Promise.all([hostedFields.tokenize(), dataCollector.getDeviceData()]);
      return { payload, deviceData };
    } catch (error: any) {
      console.error(error);
      throw parseBraintreeError(error);
    }
  }, [hostedFields, dataCollector]);

  return {
    fields: {
      number: {
        ref: numberRef,
        ...fields.number,
      },
      cvv: {
        ref: cvvRef,
        ...fields.cvv,
      },
      expirationDate: {
        ref: expDateRef,
        ...fields.expirationDate,
      },
      postalCode: {
        ref: postalCodeRef,
        ...fields.postalCode,
      },
    },
    client,
    hostedFields,
    dataCollector,
    getPayload,
  };
};

const emptyErrors: Record<BraintreeFields, string> = {
  number: "Please input card number",
  cvv: "Please input cvc",
  expirationDate: "Please input expiration date",
  postalCode: "Please input postal code",
};

const invalidErrors: Record<BraintreeFields, string> = {
  number: "Invalid card number",
  cvv: "The security for this card is invalid. Verify the code and submit the payment again.",
  expirationDate: "The security for this card is invalid. Verify the code and submit the payment again.",
  postalCode: "Invalid postal code",
};

const parseBraintreeError = (error: any): Partial<Record<BraintreeFields, string>> | string => {
  if (error?.code === "HOSTED_FIELDS_FIELDS_EMPTY") {
    return emptyErrors;
  }

  if (error?.code === "HOSTED_FIELDS_FIELDS_INVALID") {
    return ((error?.details?.invalidFieldKeys || []) as BraintreeFields[]).reduce((prev, current) => {
      return {
        ...prev,
        [current]: invalidErrors[current],
      };
    }, {});
  }

  return "Internal Error";
};

export const getBraintreeDeviceData = async () => {
  const client = await Braintree.client.create({
    authorization: import.meta.env.VITE_BRAINTREE_TOKENIZATION_KEY,
  });

  const dataCollector = await Braintree.dataCollector.create({ client });

  return (await dataCollector.getDeviceData()) as string;
};
